import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';

import { GiveBoxEvent } from '@bonusly/common-give-box';
import {
	Box,
	Button,
	Center,
	GiftWrapOutline,
	HStack,
	Image,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spinner,
	Text,
	VStack,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { useCurrentUser } from '@bonusly/who-am-i';

import { getActiveCollectibleItem } from '../../../../components/post-wrap/collectible-inventory';
import { DEFAULT_GIFT_WRAP_USES } from '../../../../config/recognition';
import { useDialogControl } from '../../../../lib/dialog';
import { getCollectibleImageUrl } from './collectible-gallery-image';

type PostPurchaseCollectibleModalStore = {
	isOpen: boolean;
	rewardId: string | null;
	onOpen: (rewardId?: string) => void;
	onClose: () => void;
};

export const usePostPurchaseCollectibleModal = create<PostPurchaseCollectibleModalStore>((set) => ({
	isOpen: false,
	rewardId: null,
	onOpen: (rewardId?: string) => set({ isOpen: true, rewardId: rewardId ?? null }),
	onClose: () => set({ isOpen: false, rewardId: null }),
}));

export const PostPurchaseCollectibleModal: FC = () => {
	const { t } = useTranslation('rewards', { keyPrefix: 'reward_details.postPurchaseCollectible' });
	const { isOpen, rewardId, onClose } = usePostPurchaseCollectibleModal();
	const user = useCurrentUser();
	const dispatch = useDispatch();
	const rewardDetails = useDialogControl('reward');

	const collectibleItem = getActiveCollectibleItem(user);
	const pattern = collectibleItem?.variant_data.pattern ?? 'stripes';
	const color = collectibleItem?.variant_data.color ?? 'teal';
	const imageUrl = getCollectibleImageUrl(pattern, color);

	const handleRecognize = () => {
		onClose();
		dispatch(GiveBoxEvent.Open);
	};

	const handleExploreOptions = () => {
		if (!rewardId) return;
		onClose();
		rewardDetails.onOpen({ rewardId, skipPostPurchase: 'true' });
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', sm: 'lg' }}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{t('title')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{!collectibleItem ? (
						<Center py={10}>
							<Spinner />
						</Center>
					) : (
						<VStack spacing={3}>
							<Box borderWidth="1px" borderColor="border.default" borderRadius="md" overflow="hidden" h="240px">
								{imageUrl && <Image src={imageUrl} alt={`${pattern} ${color} gift wrap`} w="full" h="full" objectFit="cover" />}
							</Box>

							<HStack spacing={2} w="full">
								<GiftWrapOutline boxSize={8} />
								<Text>{t('remainingCount', { count: collectibleItem.remaining_uses, total: DEFAULT_GIFT_WRAP_USES })}</Text>
							</HStack>
						</VStack>
					)}
				</ModalBody>

				<ModalFooter flexDir="column" gap={2}>
					<Button onClick={handleRecognize} w="full">
						{t('recognizeButton')}
					</Button>
					{rewardId && (
						<Button variant="link" onClick={handleExploreOptions}>
							{t('exploreOptions')}
						</Button>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
